import React, { lazy, Suspense, useEffect } from 'react';
import { ApolloProvider } from '@apollo/client';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import client from './apollo/client';
import theme from './mui/theme';
import Layout from '@features/layout/components/Layout';
import ErrorBoundary from '@components/ErrorBoundary';
import LayoutErrorBoundary from '@components/LayoutErrorBoundary';
import { useAuth } from '@features/auth/hooks/useAuth';
import { CircularProgress, LinearProgress, Box } from '@mui/material';
import GlobalSubscriptionProvider from './apollo/GlobalSubscriptionProvider';
import GlobalChatProvider from '@features/chat/components/GlobalChatProvider';
import { detectEnvironmentChange } from '@utils/cacheUtils';
import siteConfig from '@config';

// Debug flag to enable/disable global subscription for real-time move updates
const ENABLE_GLOBAL_SUBSCRIPTION = true;

// Lazy load heavy components
const AppRoutes = lazy(() => import('./Routes'));
const GoogleMapsProvider = lazy(() =>
  import('@components/maps').then(module => ({ default: module.GoogleMapsProvider }))
);

// Loading component
const AppLoader: React.FC = () => (
  <Box display='flex' justifyContent='center' alignItems='center' minHeight='100vh'>
    <CircularProgress />
  </Box>
);

const App: React.FC = () => {
  const { loading } = useAuth();

  // Initialize cache cleanup service and health reporting
  useEffect(() => {
    //console.log('🚀 Initializing cache cleanup service...');
    //cleanupService.start();

    // Start health reporting based on environment
    // if (process.env.NODE_ENV === 'production') {
    //   CacheHealthReporter.startPeriodicReporting();
    // } else {
    //   DevCacheMonitor.startMonitoring();
    // }

    // Cleanup on unmount
    return () => {
      //console.log('🛑 Stopping cache cleanup service...');
      // cleanupService.stop();
      // CacheHealthReporter.stopPeriodicReporting();
      // DevCacheMonitor.stopMonitoring();
    };
  }, []);

  // Environment change detection for cache clearing
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      // Detect environment changes and suggest cache clearing
      // Set autoClear to true if you want automatic clearing (be careful!)
      detectEnvironmentChange(siteConfig.graphqlHttpUrl, false);
    }
  }, [siteConfig.graphqlHttpUrl]);


  // Handle network state changes
  // useEffect(() => {
  //   const handleOnline = () => {
  //     console.log('🌐 Network online - forcing cache cleanup for fresh data');
  //     cleanupService.updateConfig({ enabled: true });
  //     // Force cleanup when coming back online to refresh stale data
  //     cleanupService.forceCleanup();
  //   };

  //   const handleOffline = () => {
  //     console.log('📴 Network offline - reducing cache cleanup frequency');
  //     // Reduce cleanup frequency when offline to save battery
  //     cleanupService.updateConfig({
  //       intervalMs: 15 * 60 * 1000, // 15 minutes
  //       enabled: false,
  //     });
  //   };

  //   window.addEventListener('online', handleOnline);
  //   window.addEventListener('offline', handleOffline);

  //   return () => {
  //     window.removeEventListener('online', handleOnline);
  //     window.removeEventListener('offline', handleOffline);
  //   };
  // }, []);

  const AppContent = (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Suspense fallback={<AppLoader />}>
        <GoogleMapsProvider>
          <GlobalChatProvider>
            <Layout>
              <LayoutErrorBoundary>
                {!loading && (
                  <Suspense fallback={<LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1100 }} />}>
                    <AppRoutes />
                  </Suspense>
                )}
              </LayoutErrorBoundary>
            </Layout>
          </GlobalChatProvider>
        </GoogleMapsProvider>
      </Suspense>
    </ThemeProvider>
  );

  return (
    <ErrorBoundary>
      <ApolloProvider client={client}>
        {ENABLE_GLOBAL_SUBSCRIPTION ? (
          <GlobalSubscriptionProvider>{AppContent}</GlobalSubscriptionProvider>
        ) : (
          AppContent
        )}
        {/* Development-only cache health monitor */}
        {/* {process.env.NODE_ENV === 'development' && <CacheHealthMonitor />} */}
      </ApolloProvider>
    </ErrorBoundary>
  );
};

export default App;
